import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Check, Lock } from '../../../icons';
import { toast } from '../../../components/shared';
import { layerConfirm } from '../../../components/LayerPopup/LayerPopup';
import {
  clearMenuCell, clearMenuRules, getMenuAccess, migrateUnassignedRules, saveCompanyMenus, saveMenuCell,
  type MenuAccessState,
} from '../../../api/menu-access';
import { listDivisions, listTeamMembers, listTeams, type OrgTeamMember } from '../../../api/admin-org';
import { listUsers, type UserListItem } from '../../../api/admin-user';
import type { Verdict } from '../../../lib/menu-judge';
import { OrgTree } from './OrgTree';
import { DeptTab } from './DeptTab';
import { PersonTab } from './PersonTab';
import {
  applyCell, buildOrg, orderedMenus, peopleOf, unitLabel, type OrgModel, type Person, type Unit,
} from './model';
import './menu-perm.css';
// ★ 2026-09-15 R5 — 변경 이력(서버 #1386). 시안 v4 의 머리 「변경 이력」 단추 + 아래 이력 칸.
import { History } from '../../../icons';
import type { MenuHistoryScope } from '../../../api/menu-access';
import { HistoryPanel } from './HistoryPanel';

/**
 * 통합설정 › 조직 › 메뉴 권한 — 조직 트리 + 「부서별 권한」 · 「개인별 권한」 (2026-09-15 · 확정 시안 v4).
 *
 * 시안: https://claude.ai/artifact/SuyweQ5p2EggzDtFd3QqLA · 계획: `_workspace/menu-perm/web-plan.md`.
 * 서버: PR #1370(R2 칸 단위) · #1373(R1+R7 판정 · 개인 · 팀없는직원 · moduleEnabled · viewerTeams).
 *
 * ★★ 종전 화면(`MenuPermissionSection` + `MenuMatrix`)은 **파일째 남긴다** — 이 화면이 그 자리(`CompanyAdminPanel`)만 이어받는다.
 * ★★ 저장은 **칸 하나씩** 이다. 「상속」은 DELETE(서버가 `{allow:null}` 을 400 으로 막는다).
 * ★ 뺀 것(사용자 결정): 「왜?」 · 「판정 경로」 서랍 · 「권한 확인」. 변경 이력은 서버 R5 뒤.
 * ★ 조직 트리는 기존 라우트(부서 · 팀 · 팀원 · 사용자)로 만든다 — `/organization/tree`(PR #1346)는 아직 main 에 없다.
 */

async function loadAllUsers(): Promise<UserListItem[]> {
  const acc: UserListItem[] = [];
  let cursor: string | null = null;
  for (let page = 0; page < 20; page++) {
    const r = await listUsers({ limit: 100, ...(cursor ? { cursor } : {}) });
    acc.push(...r.users);
    cursor = r.nextCursor;
    if (!cursor) break;
  }
  return acc;
}

async function pool<T, R>(items: T[], limit: number, fn: (t: T) => Promise<R>): Promise<R[]> {
  const out: R[] = new Array(items.length);
  let next = 0;
  const worker = async () => {
    while (next < items.length) {
      const i = next++;
      out[i] = await fn(items[i]!);
    }
  };
  await Promise.all(Array.from({ length: Math.max(1, Math.min(limit, items.length)) }, worker));
  return out;
}

function errMessage(e: unknown, fallback: string): string {
  const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
  return typeof msg === 'string' && msg ? msg : fallback;
}

const word = (v: boolean | null | Verdict, forUser: boolean): string => {
  if (v === null || v === 'none') return forUser ? '부서 기준' : '상속';
  return v === true || v === 'allow' ? '허용' : '차단';
};

export function MenuPermissionScreen() {
  const [state, setState] = useState<MenuAccessState | null>(null);
  const [loadError, setLoadError] = useState<'forbidden' | 'failed' | null>(null);
  const [org, setOrg] = useState<OrgModel | null>(null);
  const [orgFailed, setOrgFailed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const [unit, setUnit] = useState<Unit>({ kind: 'company' });
  const [tab, setTab] = useState<'dept' | 'person'>('dept');
  const [personId, setPersonId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let alive = true;
    void (async () => {
      setLoadError(null);
      let access: MenuAccessState;
      try {
        access = await getMenuAccess();
      } catch (e) {
        const status = (e as { response?: { status?: number } })?.response?.status;
        if (alive) setLoadError(status === 403 ? 'forbidden' : 'failed');
        return;
      }
      if (!alive) return;
      setState(access);
      try {
        const [divisions, teams, users] = await Promise.all([listDivisions(), listTeams(), loadAllUsers()]);
        const active = teams.filter((t) => t.isActive);
        const lists = await pool(active, 4, (t) => listTeamMembers(t.id).catch(() => [] as OrgTeamMember[]));
        const membersByTeam: Record<string, OrgTeamMember[]> = {};
        active.forEach((t, i) => { membersByTeam[t.id] = lists[i] ?? []; });
        if (alive) {
          setOrg(buildOrg({ divisions, teams, membersByTeam, users, viewerTeams: access.viewerTeams }));
          setOrgFailed(false);
        }
      } catch {
        if (alive) setOrgFailed(true);
      }
    })();
    return () => { alive = false; };
  }, [reloadKey]);

  const people = useMemo(() => (org ? peopleOf(org, unit) : []), [org, unit]);
  const personOverrides = state ? people.filter((p) => state.rules.some((r) => r.scopeType === 'user' && r.scopeId === p.id)).length : 0;

  // 변경 이력 — 열림 · 저장 신호. 저장이 끝나면 `saved` 가 참이 되므로 그때마다 신호를 올린다(저장 경로는 그대로).
  const [histOpen, setHistOpen] = useState(false);
  const [histTick, setHistTick] = useState(0);
  useEffect(() => { if (saved) setHistTick((t) => t + 1); }, [saved]);

  if (loadError === 'forbidden') {
    return (
      <section className="mpx" aria-label="메뉴 권한">
        <div className="mpx-empty mpx-empty--page">
          <span className="mpx-empty__ico" aria-hidden><Lock size={20} /></span>
          <b>볼 수 있는 권한이 없습니다</b>
          <p>메뉴 권한은 회사 관리자만 정할 수 있습니다.</p>
        </div>
      </section>
    );
  }
  if (loadError === 'failed') {
    return (
      <section className="mpx" aria-label="메뉴 권한">
        <div className="mpx-empty mpx-empty--page" role="alert">
          <span className="mpx-empty__ico" aria-hidden><AlertTriangle size={20} /></span>
          <b>메뉴 권한을 불러오지 못했습니다</b>
          <button type="button" className="mpx-btn" onClick={() => setReloadKey((k) => k + 1)}>다시 시도</button>
        </div>
      </section>
    );
  }
  if (!state) return <section className="mpx" aria-label="메뉴 권한" aria-busy="true"><p className="mpx-dim mpx-pad">불러오는 중…</p></section>;

  const done = (message: string) => { toast.success(message); setSaved(true); };

  async function run(action: () => Promise<void>, fallback: string): Promise<void> {
    if (busy) return;
    setBusy(true); setError(null); setSaved(false);
    try { await action(); } catch (e) { setError(errMessage(e, fallback)); } finally { setBusy(false); }
  }

  const labelOf = (menuKey: string) => state.menus.find((m) => m.menuKey === menuKey)?.label ?? menuKey;

  const onCell = (scopeType: 'team' | 'division' | 'user', scopeId: string, menuKey: string, next: Verdict) =>
    run(async () => {
      const res = next === 'none'
        ? await clearMenuCell(scopeType, scopeId, menuKey)
        : await saveMenuCell(scopeType, scopeId, menuKey, next === 'allow');
      setState((s) => (s ? applyCell(s, scopeType, scopeId, menuKey, next) : s));
      const who = scopeType === 'user' ? org?.byId.get(scopeId)?.name ?? '이 사람' : unitLabel(org, { kind: scopeType, id: scopeId });
      const forUser = scopeType === 'user';
      done(`${who} · 「${labelOf(menuKey)}」 ${word(res.previous, forUser)} → ${word(next, forUser)}`);
    }, '저장하지 못했습니다.');

  const onClearUnit = () => {
    if (unit.kind !== 'team' && unit.kind !== 'division') return;
    const name = unitLabel(org, unit);
    const n = state.rules.filter((r) => r.scopeType === unit.kind && r.scopeId === unit.id).length;
    void (async () => {
      if (!(await layerConfirm(`${name}의 규칙 ${n}개를 지웁니다 — 위 층을 그대로 받습니다.`))) return;
      await run(async () => {
        await clearMenuRules(unit.kind as 'team' | 'division', unit.id);
        setState((s) => (s ? { ...s, rules: s.rules.filter((r) => !(r.scopeType === unit.kind && r.scopeId === unit.id)) } : s));
        done(`${name}의 규칙을 지웠습니다 — 위 층을 그대로 받습니다.`);
      }, '지우지 못했습니다.');
    })();
  };

  const onClearPerson = (p: Person) => {
    const n = state.rules.filter((r) => r.scopeType === 'user' && r.scopeId === p.id).length;
    void (async () => {
      if (!(await layerConfirm(`${p.name} 님의 개인 설정 ${n}개를 되돌립니다 — 모두 부서 기준을 따릅니다.`))) return;
      await run(async () => {
        await clearMenuRules('user', p.id);
        setState((s) => (s ? { ...s, rules: s.rules.filter((r) => !(r.scopeType === 'user' && r.scopeId === p.id)) } : s));
        done(`${p.name} 님의 개인 설정을 모두 되돌렸습니다.`);
      }, '되돌리지 못했습니다.');
    })();
  };

  /** 회사 층 — 차례를 함께 보낸다(안 보내면 서버가 그 메뉴의 차례를 지운다). */
  const saveCompany = (menus: ReturnType<typeof orderedMenus>, enabledOf: (key: string) => boolean, message: string) =>
    run(async () => {
      const items = menus.map((m, i) => ({ menuKey: m.menuKey, enabled: enabledOf(m.menuKey), sortOrder: (i + 1) * 10 }));
      await saveCompanyMenus(items);
      setState((s) => (s ? { ...s, company: items.map((it) => ({ menuKey: it.menuKey, enabled: it.enabled, sortOrder: it.sortOrder })) } : s));
      done(message);
    }, '저장하지 못했습니다.');

  const companyOn = (key: string) => !state.company.some((c) => c.menuKey === key && !c.enabled);

  const onCompanyToggle = (menuKey: string, next: boolean) =>
    saveCompany(orderedMenus(state), (k) => (k === menuKey ? next : companyOn(k)),
      `「${labelOf(menuKey)}」을(를) 회사에서 ${next ? '켰습니다' : '껐습니다'}.`);

  const onMove = (menuKey: string, toIndex: number) => {
    const keys = orderedMenus(state);
    const from = keys.findIndex((m) => m.menuKey === menuKey);
    if (from < 0 || toIndex < 0 || toIndex >= keys.length || from === toIndex) return;
    const next = keys.slice();
    next.splice(toIndex, 0, ...next.splice(from, 1));
    void saveCompany(next, companyOn, '메뉴 차례를 저장했습니다.');
  };

  const onMigrate = () => {
    void (async () => {
      const teamless = org ? peopleOf(org, { kind: 'unassigned' }).length : 0;
      if (!(await layerConfirm(`남은 묶음 규칙을 지금 팀없는직원 ${teamless}명의 개인 설정으로 옮기고 묶음 규칙을 지웁니다. 나중에 팀이 없어진 사람에게는 걸리지 않습니다.`))) return;
      await run(async () => {
        const r = await migrateUnassignedRules();
        done(`팀없는직원 ${r.users}명에게 ${r.copied}칸을 옮기고 묶음 규칙 ${r.cleared}개를 지웠습니다.`);
        setReloadKey((k) => k + 1);
      }, '옮기지 못했습니다.');
    })();
  };

  const onClearLeftover = () => {
    void (async () => {
      if (!(await layerConfirm('남은 팀없는직원 묶음 규칙을 지웁니다. 판정에는 이미 쓰지 않으므로 보이는 것은 달라지지 않습니다.'))) return;
      await run(async () => {
        await clearMenuRules('unassigned', 'all');
        setState((s) => (s ? { ...s, rules: s.rules.filter((r) => r.scopeType !== 'unassigned') } : s));
        done('남은 묶음 규칙을 지웠습니다.');
      }, '지우지 못했습니다.');
    })();
  };

  const selectUnit = (u: Unit) => { setUnit(u); setPersonId(null); setSaved(false); };

  const jumpPerson = (id: string) => {
    const p = org?.byId.get(id);
    if (!p) return;
    setUnit(p.judgeTeamId ? { kind: 'team', id: p.judgeTeamId } : { kind: 'unassigned' });
    setTab('person');
    setPersonId(id);
  };

  const goPersonFor = (menuKey: string) => {
    const first = people.find((p) => state.rules.some((r) => r.scopeType === 'user' && r.scopeId === p.id && r.menuKey === menuKey));
    setTab('person');
    setPersonId(first?.id ?? null);
  };

  const kind = { company: '회사', division: '본부', team: '팀', unassigned: '팀없는직원' }[unit.kind];
  const parent = unit.kind === 'team' ? org?.teams.get(unit.id)?.divisionId : null;
  const path = unit.kind === 'company' ? null
    : unit.kind === 'team' ? (parent ? `회사 › ${org?.divisions.find((d) => d.id === parent)?.name ?? ''}` : '회사 › 본부 없음') : '회사';

  // 이력 대상 = 지금 보는 것 — 부서별 탭은 고른 조직, 개인별 탭은 고른 사람(안 골랐으면 없음).
  const histScope: MenuHistoryScope | null = tab === 'person'
    ? (personId ? { scopeType: 'user', scopeId: personId } : null)
    : unit.kind === 'company' ? { scopeType: 'company' }
      : unit.kind === 'unassigned' ? { scopeType: 'unassigned', scopeId: 'all' }
        : { scopeType: unit.kind, scopeId: unit.id };
  const histTitle = tab === 'person'
    ? (personId ? org?.byId.get(personId)?.name ?? '이 사람' : '개인별 권한')
    : unitLabel(org, unit);

  return (
    <section className="mpx" aria-label="메뉴 권한">
      <header className="mpx-head">
        <h2>메뉴 권한</h2>
        <p>누가 어떤 메뉴를 보는지 정합니다 — 개인 설정이 부서 설정보다 먼저이고, 회사에서 끈 메뉴는 누구에게도 열리지 않습니다.</p>
        <button type="button" className="mpx-btn mpx-head__hist" aria-pressed={histOpen} onClick={() => setHistOpen((v) => !v)}>
          <History size={13} aria-hidden /> 변경 이력
        </button>
      </header>

      {!state.catalogPresent ? (
        <div className="mpx-empty mpx-empty--page" role="alert">
          <span className="mpx-empty__ico" aria-hidden><AlertTriangle size={20} /></span>
          <b>메뉴 목록을 불러오지 못했습니다</b>
          <p>관리 콘솔이 내려 준 메뉴 목록이 있어야 고를 수 있습니다.</p>
          <button type="button" className="mpx-btn" onClick={() => setReloadKey((k) => k + 1)}>다시 시도</button>
        </div>
      ) : (
        <div className="mpx-body">
          <OrgTree org={org} orgFailed={orgFailed} state={state} unit={unit} onSelectUnit={selectUnit} onJumpPerson={jumpPerson} />

          <div className="mpx-pane">
            <div className="mpx-pane__top">
              <div className="mpx-crumb">
                <h3>{unitLabel(org, unit)}</h3>
                <span className="mpx-kind">{kind}</span>
                {path && <span className="mpx-dim">{path}</span>}
                <span className="mpx-sp" />
                {saved && <span className="mpx-saved" role="status"><Check size={12} aria-hidden /> 저장됨 · 방금</span>}
                {org && <span className="mpx-dim">{people.length}명</span>}
              </div>
              <div className="mpx-tabs" role="tablist" aria-label="권한 종류">
                <button type="button" role="tab" id="mpx-tab-dept" aria-controls="mpx-panel" aria-selected={tab === 'dept'}
                  className="mpx-tab" onClick={() => setTab('dept')}>부서별 권한</button>
                <button type="button" role="tab" id="mpx-tab-person" aria-controls="mpx-panel" aria-selected={tab === 'person'}
                  className="mpx-tab" onClick={() => setTab('person')}>
                  개인별 권한{personOverrides > 0 && <span className="mpx-dim"> 개인 설정 {personOverrides}명</span>}
                </button>
              </div>
            </div>

            {error && <p className="mpx-note mpx-note--bad" role="alert"><AlertTriangle size={14} aria-hidden /> {error}</p>}

            <div id="mpx-panel" role="tabpanel" aria-labelledby={tab === 'dept' ? 'mpx-tab-dept' : 'mpx-tab-person'} className="mpx-panel">
              {tab === 'dept' ? (
                <DeptTab
                  state={state} org={org} unit={unit} busy={busy}
                  onCell={(t, id, k, v) => { void onCell(t, id, k, v); }}
                  onClearUnit={onClearUnit}
                  onCompanyToggle={(k, v) => { void onCompanyToggle(k, v); }}
                  onMove={onMove}
                  onMigrate={onMigrate}
                  onClearLeftover={onClearLeftover}
                  onGoPerson={goPersonFor}
                />
              ) : org ? (
                <PersonTab
                  state={state} org={org} unit={unit} personId={personId} busy={busy}
                  onSelectPerson={setPersonId}
                  onCell={(id, k, v) => { void onCell('user', id, k, v); }}
                  onClearPerson={onClearPerson}
                />
              ) : (
                <p className="mpx-dim mpx-pad">{orgFailed ? '조직을 불러오지 못해 사람을 고를 수 없습니다.' : '사람을 불러오는 중…'}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {histOpen && <HistoryPanel scope={histScope} title={histTitle} refreshTick={histTick} />}
    </section>
  );
}

export default MenuPermissionScreen;
